/**
 * OrderScreen — the online-ordering experience at /order (and /kiosk).
 *
 * The live Square menu (useSquareMenu) laid out for whatever is holding it:
 *
 *   phone             search + a sticky category rail, one column of items,
 *                     a cart bar at the bottom that opens the cart.
 *   tablet portrait   the same with two columns.
 *   tablet landscape  categories down the left, the menu in the middle and the
 *   / desktop         cart docked on the right — nothing to open or close.
 *
 * Most of the catalog has no photo, so items are text-first rows that gain a
 * thumbnail when one exists; photographed and manager-badged items also lead
 * the page in a "Popular" rail. Simple items add with one tap (and then show a
 * − / + stepper); anything with choices opens the configurator.
 *
 * Default export `OrderPage` wires the OrderingProvider and the cart/checkout
 * modals. Access follows the backend payment environment: open to everyone in
 * production, staff-only while the backend is in sandbox test mode. With
 * `kiosk` it is the shared ordering tablet: no site chrome, nothing remembered
 * between customers, and an idle screen wipes itself.
 */
import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { auth } from '../../../firebase';
import { OrderingProvider, useOrdering } from './OrderingContext';
import useSquareMenu from '../hooks/useSquareMenu';
import { getStoreStatus } from '../components/StoreStatus';
import { fmt$, setItemAvailability } from './orderApi';
import CartDrawer, { CartPanel } from './CartDrawer';
import CheckoutModal from './CheckoutModal';
import ItemConfigModal from './ItemConfigModal';
import HistoryModal from './HistoryModal';
import OrdersAdminPanel from './OrdersAdminPanel';
import OrderTracker from './OrderTracker';
import { favoriteItems, buildReorderCart, needsConfiguration, defaultConfig } from './orderHistory';
import { featuredItems, priceOf, matchesQuery, activeOrder, isFinalStage, reconcileCart } from './orderFlow';
import './ordering.css';
import './orderScreen.css';

const cents$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const DOCK_QUERY = '(min-width: 1100px)';
const MENU_STALE_MS = 3 * 60 * 1000;
const KIOSK_IDLE_MS = 90 * 1000;
const KIOSK_WARN_S = 20;

// Customer-facing labels for Menu Manager badges (keys match square-catalog-admin).
const BADGE_LABELS = {
  popular: '🔥 Popular', new: '✨ New', bestseller: '⭐ Best seller',
  chef: "👨‍🍳 Chef's pick", spicy: '🌶️ Spicy', veg: '🟢 Veg', glutenfree: '🌾 GF',
};

// Photo URLs that have actually loaded (shared across mounts). A menu photo is
// only shown once it has — several curated URLs point at files that were never
// uploaded, and a row of empty photo frames is worse than no photos.
const LOADED_IMAGES = new Set();
const PROBED_IMAGES = new Set();

function useLoadedImages(categories) {
  const [loaded, setLoaded] = useState(() => new Set(LOADED_IMAGES));
  useEffect(() => {
    let alive = true;
    (categories || []).forEach(c => (c.items || []).forEach(({ imageUrl }) => {
      if (!imageUrl || PROBED_IMAGES.has(imageUrl)) return;
      PROBED_IMAGES.add(imageUrl);
      const img = new Image();
      img.onload = () => { LOADED_IMAGES.add(imageUrl); if (alive) setLoaded(new Set(LOADED_IMAGES)); };
      img.src = imageUrl;
    }));
    // A remount picks up anything that finished loading in between.
    setLoaded(prev => (prev.size === LOADED_IMAGES.size ? prev : new Set(LOADED_IMAGES)));
    return () => { alive = false; };
  }, [categories]);
  return loaded;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

/**
 * One menu item. `lineKey` is the cart line a − / + stepper can drive: set only
 * when the item needs no choices and sits in the cart as a single plain line.
 */
const ItemCard = React.memo(function ItemCard({
  item, inCart, lineKey, isStaff, toggling, onOpen, onAdd, onInc, onDec, onToggleAvailability,
}) {
  const { cents, from } = priceOf(item);
  const badges = (item.badges || []).map(b => BADGE_LABELS[b]).filter(Boolean).slice(0, 2);

  return (
    <article className={`osc-item${item.soldOut ? ' osc-item--off' : ''}${inCart > 0 ? ' osc-item--in' : ''}`}>
      <button className="osc-item__body" onClick={() => onOpen(item)} disabled={item.soldOut} aria-label={`${item.name}, ${cents$(cents)}`}>
        <span className="osc-item__name">
          {inCart > 0 && <span className="osc-item__count">{inCart}×</span>}
          {item.name}
        </span>
        {item.description && <span className="osc-item__desc">{item.description}</span>}
        <span className="osc-item__meta">
          <span className="osc-item__price">{from ? 'from ' : ''}{cents$(cents)}</span>
          {badges.map(b => <span key={b} className="osc-badge">{b}</span>)}
        </span>
      </button>

      <div className="osc-item__side">
        {item.imageUrl && (
          <button className="osc-item__img" style={{ backgroundImage: `url(${item.imageUrl})` }}
            onClick={() => onOpen(item)} disabled={item.soldOut} tabIndex={-1} aria-hidden="true" />
        )}
        {item.soldOut ? (
          <span className="osc-item__sold">Sold out</span>
        ) : lineKey ? (
          <div className="osc-step" role="group" aria-label={`${item.name} quantity`}>
            <button onClick={() => onDec(lineKey)} aria-label={inCart === 1 ? 'Remove' : 'One less'}>−</button>
            <span>{inCart}</span>
            <button onClick={() => onInc(lineKey)} aria-label="One more">+</button>
          </div>
        ) : (
          <button className="osc-item__add" onClick={() => onAdd(item)} aria-label={`Add ${item.name}`}>＋</button>
        )}
      </div>

      {isStaff && (
        <button
          className={`osc-86${item.soldOut ? ' osc-86--out' : ''}`}
          onClick={() => onToggleAvailability(item)}
          disabled={toggling}
          title={item.soldOut ? 'Mark as available' : 'Mark as sold out (86)'}>
          {toggling ? '…' : item.soldOut ? 'Restock' : '86'}
        </button>
      )}
    </article>
  );
});

/** Photo-forward card for the "Popular" rail. */
function FeaturedCard({ item, onOpen, onAdd }) {
  const { cents, from } = priceOf(item);
  return (
    <div className="osc-feat">
      <button className="osc-feat__media" onClick={() => onOpen(item)} aria-label={item.name}
        style={item.imageUrl ? { backgroundImage: `url(${item.imageUrl})` } : undefined}>
        {!item.imageUrl && <span aria-hidden="true">{item.emoji || '🍽️'}</span>}
      </button>
      <div className="osc-feat__info">
        <span className="osc-feat__name">{item.name}</span>
        <span className="osc-feat__price">{from ? 'from ' : ''}{cents$(cents)}</span>
      </div>
      <button className="osc-item__add osc-feat__add" onClick={() => onAdd(item)} aria-label={`Add ${item.name}`}>＋</button>
    </div>
  );
}

/** Compact "your usuals" quick-add chip. */
function FavChip({ fav, onAdd }) {
  const { item } = fav;
  return (
    <button className="osc-fav" onClick={() => onAdd(item)} title={`Add ${item.name}`}>
      <span className="osc-fav__name">{item.name}</span>
      <span className="osc-fav__price">{cents$(priceOf(item).cents)}</span>
      <span className="osc-fav__plus" aria-hidden="true">＋</span>
    </button>
  );
}

/** Kiosk only: an abandoned order clears itself so the next customer starts clean. */
function KioskGuard() {
  const { cart, drawerOpen, checkoutOpen, activeItem, paying, resetSession } = useOrdering();
  const dirty = cart.count > 0 || drawerOpen || checkoutOpen || !!activeItem;
  const [left, setLeft] = useState(null); // seconds until reset, while warning
  const last = useRef(Date.now());

  useEffect(() => {
    const bump = () => { last.current = Date.now(); };
    // blur/focus: taps inside Square's card iframe never reach this window.
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll', 'blur', 'focus'];
    events.forEach(ev => window.addEventListener(ev, bump, { passive: true, capture: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, bump, { capture: true }));
  }, []);

  useEffect(() => {
    if (!dirty || paying) { setLeft(null); return undefined; }
    last.current = Date.now();
    const t = setInterval(() => {
      const idle = Date.now() - last.current;
      if (idle >= KIOSK_IDLE_MS + KIOSK_WARN_S * 1000) { setLeft(null); resetSession(); }
      else if (idle >= KIOSK_IDLE_MS) setLeft(Math.ceil((KIOSK_IDLE_MS + KIOSK_WARN_S * 1000 - idle) / 1000));
      else setLeft(null);
    }, 1000);
    return () => clearInterval(t);
  }, [dirty, paying, resetSession]);

  if (left == null) return null;
  return (
    <div className="ord-overlay osc-idle" role="alertdialog" aria-label="Still there?">
      <div className="ord-modal osc-idle__box">
        <h3>Still there?</h3>
        <p>This order will clear in <strong>{left}</strong> seconds.</p>
        <button className="ord-btn ord-btn--primary" onClick={() => { last.current = Date.now(); setLeft(null); }}>I'm still ordering</button>
        <button className="ord-btn ord-btn--ghost" onClick={() => { setLeft(null); resetSession(); }}>Start over</button>
      </div>
    </div>
  );
}

function OrderScreen() {
  const {
    canOrder, isStaff, kiosk, gateLoading, cart, openCart, setActiveItem, notify, notice,
    history, updateOrder, setHistoryOpen, setOrdersOpen, setMenuCategories, setCartDocked, resetSession,
  } = useOrdering();
  // Customers share the edge-cached menu; staff read it live so an 86 shows at once.
  const { categories, loading, error, refetch } = useSquareMenu({ shared: !isStaff });
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [toggleErr, setToggleErr] = useState('');
  const [store, setStore] = useState(getStoreStatus);
  const [topOffset, setTopOffset] = useState(0);
  const sectionRefs = useRef({});
  const navRef = useRef(null);
  const catsRef = useRef(null);
  const fetchedAt = useRef(Date.now());
  const docked = useMediaQuery(DOCK_QUERY);

  useEffect(() => { setCartDocked(docked); return () => setCartDocked(false); }, [docked, setCartDocked]);

  useEffect(() => {
    const t = setInterval(() => setStore(getStoreStatus()), 60000);
    return () => clearInterval(t);
  }, []);

  // The site header is sticky; everything sticky here sits just under it.
  useLayoutEffect(() => {
    const bar = kiosk ? null : document.querySelector('.top-bar');
    if (!bar) { setTopOffset(0); return undefined; }
    const measure = () => setTopOffset(bar.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [kiosk]);

  // Coming back to the tab after a while: prices and sold-outs may have moved.
  // `menuFresh` turns true once a fetch has finished on this visit — until then
  // the menu on screen may be the copy cached earlier in the session.
  const [menuFresh, setMenuFresh] = useState(false);
  const sawLoading = useRef(false);
  useEffect(() => {
    if (loading) { sawLoading.current = true; return; }
    fetchedAt.current = Date.now();
    if (sawLoading.current && !error) setMenuFresh(true);
  }, [loading, error]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - fetchedAt.current > MENU_STALE_MS) refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  // Staff-only: 86 / restock an item. Writes the Square sold-out override
  // (same as the POS button), then refetches the menu past the server cache.
  const togglingRef = useRef(null);
  const toggleAvailability = useCallback(async (item) => {
    if (togglingRef.current) return;
    togglingRef.current = item.id;
    setTogglingId(item.id);
    setToggleErr('');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Sign in again to change availability.');
      await setItemAvailability({ itemId: item.id, itemName: item.name, soldOut: !item.soldOut, idToken });
      await refetch(true);
    } catch (e) {
      setToggleErr(`${item.name}: ${e.message || 'could not update availability'}`);
    } finally {
      togglingRef.current = null;
      setTogglingId(null);
    }
  }, [refetch]);

  // Visible categories = those with at least one online-visible item.
  const loadedImages = useLoadedImages(categories);
  const cats = useMemo(() => (categories || [])
    .map(c => ({
      ...c,
      items: (c.items || [])
        .filter(i => i.ecomVisible !== false)
        .map(i => (i.imageUrl && !loadedImages.has(i.imageUrl) ? { ...i, imageUrl: null } : i)),
    }))
    .filter(c => c.items.length > 0), [categories, loadedImages]);

  // Share the live menu with the drawer/history modal (suggestions, reorder).
  // Functional update bails out while both are empty (menu still loading).
  useEffect(() => {
    setMenuCategories(prev => (prev.length === 0 && cats.length === 0 ? prev : cats));
  }, [cats, setMenuCategories]);

  // A cart saved on an earlier visit follows today's menu: sold-out or removed
  // items drop out (and are named), moved prices are corrected. Only against a
  // freshly fetched menu, and only when the menu changes — not on every cart edit.
  const replaceCart = cart.replace;
  const cartItemsRef = useRef(cart.items);
  cartItemsRef.current = cart.items;
  useEffect(() => {
    if (!menuFresh || cats.length === 0) return;
    const { items, repriced, removed } = reconcileCart(cartItemsRef.current, cats);
    if (items === cartItemsRef.current) return;
    replaceCart(items);
    if (removed.length) notify(`No longer available, removed from your cart: ${removed.join(', ')}`);
    else if (repriced) notify("Your cart was updated to today's prices.");
  }, [menuFresh, cats, replaceCart, notify]);

  const featured = useMemo(() => featuredItems(cats, 10), [cats]);
  // "Your usuals" — most-ordered items on this device, still in stock.
  const favorites = useMemo(() => favoriteItems(history, cats, 8), [history, cats]);
  const lastOrder = history[0] || null;
  const tracking = useMemo(() => activeOrder(history), [history]);

  // Per menu item: how many are in the cart, and — when that is one plain line
  // — the line a stepper on the card can drive.
  const cartByItem = useMemo(() => {
    const map = new Map();
    (cart.items || []).forEach(it => {
      if (!it.catalogObjectId) return;
      const cur = map.get(it.catalogObjectId) || { qty: 0, lines: [] };
      cur.qty += it.qty;
      cur.lines.push(it);
      map.set(it.catalogObjectId, cur);
    });
    return map;
  }, [cart.items]);

  const addConfigured = cart.addConfigured;
  // One-tap add: straight to cart unless the item needs choices.
  const quickAdd = useCallback((item) => {
    if (needsConfiguration(item)) { setActiveItem(item); return; }
    const cfg = defaultConfig(item);
    if (cfg) { addConfigured(cfg, 1); notify(`Added ${item.name}`); }
  }, [addConfigured, notify, setActiveItem]);

  // Rebuild a past order in the cart at current prices/availability.
  const reorder = (order) => {
    const { adds, skipped } = buildReorderCart(order, cats);
    adds.forEach(({ cfg, qty }) => cart.addConfigured(cfg, qty));
    if (adds.length) openCart();
    if (skipped.length) notify(`Not available right now: ${skipped.join(', ')}.`);
    else if (!adds.length) notify('None of those items are available right now.');
    else notify('Your last order is in the cart.');
  };

  const searching = query.trim().length > 0;
  const shown = useMemo(() => (searching
    ? cats.map(c => ({ ...c, items: c.items.filter(i => matchesQuery(i, query)) })).filter(c => c.items.length > 0)
    : cats), [cats, query, searching]);
  const resultCount = useMemo(() => shown.reduce((s, c) => s + c.items.length, 0), [shown]);

  // Where a section's heading has to stop to clear what is stuck above it.
  const stickLine = useCallback(
    () => topOffset + (docked ? 16 : (navRef.current?.offsetHeight || 0) + 8),
    [topOffset, docked]
  );

  // Scroll-spy: the category whose section is at the top of the viewport.
  useEffect(() => {
    if (loading || !shown.length) return undefined;
    let timer = 0;
    const update = () => {
      timer = 0;
      const line = stickLine() + 24;
      let current = shown[0].id;
      for (const c of shown) {
        const el = sectionRefs.current[c.id];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) current = c.id; else break;
      }
      setActiveCat(current);
    };
    // Throttled with a timer, not requestAnimationFrame: rAF never fires while
    // the page isn't being painted, which left the rail stuck on a stale category.
    const onScroll = () => { if (!timer) timer = setTimeout(update, 80); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => { clearTimeout(timer); window.removeEventListener('scroll', onScroll); };
  }, [shown, loading, stickLine]);

  // Keep the active category visible in its rail (sideways on phones, up/down docked).
  useEffect(() => {
    const rail = catsRef.current;
    const chip = rail?.querySelector('[aria-current="true"]');
    if (!rail || !chip || !rail.scrollTo) return;
    rail.scrollTo({
      left: chip.offsetLeft - (rail.clientWidth - chip.clientWidth) / 2,
      top: chip.offsetTop - (rail.clientHeight - chip.clientHeight) / 2,
      behavior: document.visibilityState === 'visible' ? 'smooth' : 'auto',
    });
  }, [activeCat]);

  const goToCat = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    setActiveCat(id);
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - stickLine(), behavior: 'smooth' });
  };

  if (gateLoading) {
    return <div className="osc-state"><div className="osc-spin" /><p>Loading online ordering…</p></div>;
  }

  if (!canOrder) {
    return (
      <div className="osc-gate">
        <span>🛍</span>
        <h2>Online ordering is almost here</h2>
        <p>We're putting the finishing touches on ordering directly from our site. Until then, order through DoorDash or give us a call!</p>
        <div className="osc-gate__actions">
          <a
            className="ord-btn ord-btn--primary"
            href="https://www.doordash.com/store/desi-bites-centerton-31061357"
            target="_blank" rel="noopener noreferrer">
            Order on DoorDash
          </a>
          <a className="ord-btn ord-btn--ghost" href="tel:+14794129916">Call (479) 412-9916</a>
        </div>
      </div>
    );
  }

  const cardProps = {
    isStaff, onOpen: setActiveItem, onAdd: quickAdd, onInc: cart.inc, onDec: cart.dec,
    onToggleAvailability: toggleAvailability,
  };
  const renderItem = (item) => {
    const inCart = cartByItem.get(item.id);
    const only = inCart?.lines.length === 1 ? inCart.lines[0] : null;
    const plain = only && !needsConfiguration(item) && !(only.modifiers || []).length;
    return (
      <ItemCard
        key={item.id} item={item}
        inCart={inCart?.qty || 0}
        lineKey={plain ? only.key : null}
        toggling={togglingId === item.id}
        {...cardProps}
      />
    );
  };

  return (
    <div className={`osc-page${kiosk ? ' osc-page--kiosk' : ''}`} style={{ '--osc-top': `${topOffset}px` }}>
      <header className="osc-hero">
        <div className="osc-hero__text">
          <span className="osc-eyebrow">Desi Bites · Centerton, AR</span>
          <h1 className="osc-title">{kiosk ? 'Order here' : 'Order for pickup'}</h1>
          <div className="osc-hero__meta">
            <span className={`osc-pill ${store.isOpen ? 'osc-pill--open' : 'osc-pill--closed'}`}>
              <i aria-hidden="true" />{store.isOpen ? 'Open' : 'Closed'} · {store.message}
            </span>
            <span className="osc-pill">⏱ Ready in about 20 min</span>
            {!kiosk && <span className="osc-pill osc-pill--wide">📍 226 N Main St</span>}
          </div>
        </div>
        <div className="osc-hero__actions">
          {isStaff && (
            <button className="osc-btn" onClick={() => setOrdersOpen(true)} title="Manage today's online orders">🧾 Orders</button>
          )}
          {history.length > 0 && (
            <button className="osc-btn" onClick={() => setHistoryOpen(true)}>🕘 My orders</button>
          )}
          {isStaff && (
            <button className="osc-btn osc-btn--icon" onClick={() => refetch(true)} title="Refresh menu" aria-label="Refresh menu">↻</button>
          )}
          {kiosk && cart.count > 0 && (
            <button className="osc-btn" onClick={resetSession}>Start over</button>
          )}
        </div>
      </header>

      <div className="osc-layout">
        {/* Search + categories: a sticky bar on phones, a sticky column docked */}
        <div className="osc-nav" ref={navRef}>
          <div className="osc-search">
            <span className="osc-search__icon" aria-hidden="true">🔍</span>
            <input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search the menu" aria-label="Search the menu" enterKeyHint="search" />
            {query && <button className="osc-search__clear" onClick={() => setQuery('')} aria-label="Clear search">✕</button>}
          </div>
          {!loading && !searching && cats.length > 0 && (
            <nav className="osc-cats" ref={catsRef} aria-label="Menu categories">
              {cats.map(c => (
                <button
                  key={c.id}
                  className={`osc-cat${activeCat === c.id ? ' osc-cat--on' : ''}`}
                  aria-current={activeCat === c.id ? 'true' : undefined}
                  onClick={() => goToCat(c.id)}>
                  <span>{c.name}</span>
                  <em>{c.items.length}</em>
                </button>
              ))}
            </nav>
          )}
        </div>

        <main className="osc-main">
          {toggleErr && <div className="osc-toggle-err" role="alert">⚠ {toggleErr}</div>}

          {/* The order this device placed most recently, while it's still live */}
          {tracking && !searching && (
            <section className="osc-track">
              <OrderTracker
                compact orderId={tracking.orderId}
                onChange={st => updateOrder(tracking.orderId, { stage: st.stage, done: isFinalStage(st.stage) })} />
            </section>
          )}

          {/* Order again — favorites + one-tap reorder of the last order */}
          {!loading && !searching && (favorites.length > 0 || lastOrder) && (
            <section className="osc-again">
              <div className="osc-again__head">
                <h2 className="osc-section__title">Order again</h2>
                <button className="osc-again__all" onClick={() => setHistoryOpen(true)}>My orders ›</button>
              </div>
              {lastOrder && (
                <button className="osc-lastorder" onClick={() => reorder(lastOrder)}>
                  <span className="osc-lastorder__main">
                    <strong>Reorder your last order</strong>
                    <small>
                      {lastOrder.items.map(i => `${i.qty}× ${i.name}`).join(', ').slice(0, 90)}
                      {lastOrder.totalCents != null ? ` · ${cents$(lastOrder.totalCents)}` : ''}
                    </small>
                  </span>
                  <span className="osc-lastorder__go" aria-hidden="true">↻</span>
                </button>
              )}
              {favorites.length > 0 && (
                <div className="osc-favs">
                  {favorites.map(f => <FavChip key={f.item.id} fav={f} onAdd={quickAdd} />)}
                </div>
              )}
            </section>
          )}

          {!loading && !searching && featured.length > 0 && (
            <section className="osc-featured">
              <h2 className="osc-section__title">Popular</h2>
              <div className="osc-featured__row">
                {featured.map(item => <FeaturedCard key={item.id} item={item} onOpen={setActiveItem} onAdd={quickAdd} />)}
              </div>
            </section>
          )}

          {loading && cats.length === 0 ? (
            <div className="osc-grid" aria-busy="true" aria-label="Loading the menu">
              {Array.from({ length: 8 }, (_, i) => <div key={i} className="osc-item osc-item--skel" />)}
            </div>
          ) : error && cats.length === 0 ? (
            <div className="osc-state osc-state--err">
              <p>⚠ We couldn't load the menu. {error}</p>
              <button className="ord-btn ord-btn--ghost" onClick={() => refetch()}>Try again</button>
            </div>
          ) : shown.length === 0 ? (
            <div className="osc-state">
              <p>Nothing matches “{query}”.</p>
              <button className="ord-btn ord-btn--ghost" onClick={() => setQuery('')}>Show the whole menu</button>
            </div>
          ) : (
            <div className="osc-sections">
              {searching && <p className="osc-results">{resultCount} result{resultCount === 1 ? '' : 's'} for “{query.trim()}”</p>}
              {shown.map(cat => (
                <section key={cat.id} ref={el => { sectionRefs.current[cat.id] = el; }} className="osc-section">
                  <h2 className="osc-section__title">
                    {cat.name}
                    <span className="osc-section__count">{cat.items.length}</span>
                  </h2>
                  <div className="osc-grid">{cat.items.map(renderItem)}</div>
                </section>
              ))}
            </div>
          )}
        </main>

        {docked && (
          <aside className="osc-side" aria-label="Your order">
            <CartPanel docked />
          </aside>
        )}
      </div>

      {!docked && cart.count > 0 && (
        <button className="osc-cartbar" onClick={openCart}>
          <span className="osc-cartbar__count">{cart.count}</span>
          <span className="osc-cartbar__label">View order</span>
          <span className="osc-cartbar__total">{fmt$(cart.subtotal)}</span>
        </button>
      )}

      {notice && <div className={`osc-toast${!docked && cart.count > 0 ? ' osc-toast--raised' : ''}`} role="status">{notice}</div>}
    </div>
  );
}

export default function OrderPage({ kiosk = false }) {
  const { role } = useAuth();

  // The kiosk is only the ordering screen — hide the site header, nav and footer.
  useEffect(() => {
    if (!kiosk) return undefined;
    document.body.classList.add('osc-kiosk');
    return () => document.body.classList.remove('osc-kiosk');
  }, [kiosk]);

  return (
    <OrderingProvider role={role} kiosk={kiosk}>
      {/* osc-dark scopes the dark ordering theme (overrides the ord- tokens) */}
      <div className="osc-dark">
        <OrderScreenSession />
        <CartDrawer />
        <CheckoutModal />
        <ItemConfigModal />
        <HistoryModal />
        <OrdersAdminPanel />
        {kiosk && <KioskGuard />}
      </div>
    </OrderingProvider>
  );
}

/** Remounts the screen on a kiosk reset so search and scroll start over too. */
function OrderScreenSession() {
  const { sessionId } = useOrdering();
  return <OrderScreen key={sessionId} />;
}
